import axios from 'axios'
import { Loading } from 'element-ui'
import config from '@/conf/config'
import { getHeader } from './header'

// 创建loading实例
let loadingInstance = null

// 401 防重复跳转标志
let isRedirecting = false

// 富文本字段白名单（这些字段可能包含合法HTML，不做XSS过滤）
const RICH_TEXT_KEYS = ['context', 'content', 'noticeContent', 'description', 'remark', 'templateContent']

/**
 * XSS 入参过滤 - 过滤危险模式
 */
function xssFilter(value) {
    if (typeof value !== 'string') return value
    return value
        .replace(/<script[\s/>]/gi, '&lt;script ')
        .replace(/<\/script[\s>]/gi, '&lt;/script ')
        .replace(/javascript:/gi, 'javascript&#58;')
        .replace(/[\s/]on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '')
        .replace(/v-bind:/gi, 'v-bind&#58;')
}

/**
 * 递归过滤对象中的字符串值
 */
function filterObjectXss(obj, parentKey) {
    if (obj === null || obj === undefined) return obj
    if (typeof obj === 'string') return xssFilter(obj)
    if (Array.isArray(obj)) {
        return obj.map(item => filterObjectXss(item, parentKey))
    }
    if (typeof obj === 'object') {
        // 跳过 FormData
        if (obj instanceof FormData) return obj
        const result = {}
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                // 富文本字段不过滤
                if (RICH_TEXT_KEYS.includes(key)) {
                    result[key] = obj[key]
                } else {
                    result[key] = filterObjectXss(obj[key], key)
                }
            }
        }
        return result
    }
    return obj
}

/**
 * 参数长度检查 - 20000字符上限
 */
const MAX_PARAM_LENGTH = 20000

function checkParamLength(obj, path) {
    if (obj === null || obj === undefined) return true
    if (typeof obj === 'string') {
        if (obj.length > MAX_PARAM_LENGTH) {
            console.error(`参数超长: ${path} 长度 ${obj.length} 超过上限 ${MAX_PARAM_LENGTH}`)
            return false
        }
        return true
    }
    if (Array.isArray(obj)) {
        return obj.every((item, i) => checkParamLength(item, `${path}[${i}]`))
    }
    if (typeof obj === 'object') {
        if (obj instanceof FormData) return true
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                if (!checkParamLength(obj[key], `${path}.${key}`)) return false
            }
        }
        return true
    }
    return true
}

// 创建axios实例
const service = axios.create({
  baseURL: '/',
  timeout: config.apiTimeout
})

// 显示加载动画
const showLoading = () => {
  loadingInstance = Loading.service({
    lock: true,
    text: '加载中...',
    spinner: 'el-icon-loading',
    background: 'rgba(0, 0, 0, 0.7)'
  })
}

// 隐藏加载动画
const hideLoading = () => {
  if (loadingInstance) {
    loadingInstance.close()
    loadingInstance = null
  }
}

// 请求拦截器
service.interceptors.request.use(
  config => {
    let _header = getHeader();
    config.headers['Content-Type'] = 'application/json'
    config.headers['Accept'] = 'application/json'
    config.headers['app-id'] = _header['app-id']
    config.headers['TRANSACTION-ID'] = _header['TRANSACTION-ID']
    config.headers['REQ-TIME'] = _header['REQ-TIME']
    config.headers['SIGN'] = _header['SIGN']
    config.headers['user-id'] = _header['user-id']
    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    config.headers['Authorization'] = _header['Authorization']
    if (config.url.indexOf('/app/') == -1 && config.url.indexOf('/callComponent/') == -1) {
      config.baseURL = "/app"
    }

    // 参数长度检查
    if (config.params && !checkParamLength(config.params, 'params')) {
      hideLoading()
      return Promise.reject(new Error('请求参数长度超过上限（' + MAX_PARAM_LENGTH + '字符）'))
    }
    if (config.data && !(config.data instanceof FormData) && !checkParamLength(config.data, 'data')) {
      hideLoading()
      return Promise.reject(new Error('请求参数长度超过上限（' + MAX_PARAM_LENGTH + '字符）'))
    }

    // XSS 入参过滤（排除 FormData）
    if (config.params) {
      config.params = filterObjectXss(config.params)
    }
    if (config.data && !(config.data instanceof FormData)) {
      config.data = filterObjectXss(config.data)
    }

    // GET请求不显示加载动画，其他请求显示
    if (config.method !== 'get' && config.showLoading !== false) {
      showLoading()
    }

    return config
  },
  error => {
    console.log(error)
    hideLoading()
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    hideLoading()
    return response
  },
  error => {
    console.log(error)
    hideLoading()

    if (error.response && error.response.status === 401) {
      // 清除所有敏感数据
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('hc_staff_privilege')
      localStorage.removeItem('hc_communityInfos')
      localStorage.removeItem('hc_currentCommunityInfo')

      // 防重复跳转
      if (!isRedirecting) {
        isRedirecting = true
        window.location.href = '/#/views/user/login'
        setTimeout(() => { isRedirecting = false }, 3000)
      }
    }

    return Promise.reject(error)
  }
)

export default service
